<template>
  <Overlay
    width="large"
    @destroy="closeOverlay">

    <h2 class="overlay-heading">Select a {{heading}}</h2>
    <h3>
      <span v-if="allFontsLoaded">All {{fonts.length}} Loaded!</span>
      <span v-else>{{fontIndex}}/{{fonts.length}} Loaded</span>
    </h3>

    <div class="font-controls">
      <span>Sort By:</span>
      <Btn
        @click="sortFonts('popularity')"
        :class="{'is-active': filterActive('popularity')}"
        subtle>
        Most Popular
      </Btn>
      <Btn
        @click="sortFonts('alpha')"
        :class="{'is-active': filterActive('alpha')}"
        subtle>
        Alphabetically</Btn
        >
      <Btn
        @click="sortFonts('date')"
        :class="{'is-active': filterActive('date')}"
        subtle>
        Newest Fonts
      </Btn>
      <Btn
        @click="sortFonts('style')"
        :class="{'is-active': filterActive('style')}"
        subtle>
        By Style
      </Btn>
      <Btn
        @click="sortFonts('trending')"
        :class="{'is-active': filterActive('trending')}"
        subtle>
        Trending Now
      </Btn>

      <a
        class="btn is-tertiary"
        href="https://fonts.google.com/?preview.text=PageMakerPro&preview.text_type=custom" target="_blank">
        View On Google
      </a>
    </div>

    <transition name="fade" mode="out-in">
      <ul class="font-list" v-if="initialFontsLoaded" key="fonts-loaded">
        <li
          @click="setFont(font)"
          v-for="(font, index) in fonts"
          :style="fontStyle(font)"
          :key="`font-${index}`">
          {{font.family}}
        </li>
      </ul>

      <div class="loading-fonts" v-else key="fonts-loading">
        <p>One moment, we're just loading the fonts...</p>
      </div>
    </transition>
  </Overlay>
</template>

<script>
import Ajax from 'ajax/src/ajax'
import Overlay from '@/components/Overlay'
import Btn from '@/components/Btn'

const CLIENT_ID = process.env.VUE_APP_GOOGLE_KEY

export default {
  name: 'font-previewer',

  data() {
    return {
      activeFont: '',
      fontIndex: 0,
      initialFontsLoaded: false,
      isSorting: false,
      sortBy: 'popularity',
      fonts: []
    }
  },

  props: {
    type: String
  },

  components: {
    Overlay,
    Btn
  },

  computed: {
    url() {
      const base = 'https://www.googleapis.com/webfonts/v1/webfonts'
      const clientIdParam = `?key=${CLIENT_ID}`
      let params = `&sort=${this.sortBy}`

      return base + clientIdParam + params
    },

    filterActive() {
      return (type) => {
        return type === this.sortBy
      }
    },

    heading() {
      if (this.type === 'font_body') {
        return 'Body Font'
      } else {
        return 'Heading Font'
      }
    },

    fontStyle() {
      return (font) => {
        return `font-family: "${font.family}"`
      }
    },

    allFontsLoaded() {
      return this.fontIndex === this.fonts.length - 1 || false
    }
  },

  methods: {
    getFonts() {
      Ajax.request({
        url: this.url,

        onSuccess: (xhr) => {
          this.fonts = JSON.parse(xhr.responseText).items
          this.addStyles()
        },

        onError: (xhr) => {
          alert(xhr)
        }
      })
    },

    closeOverlay() {
      this.$emit('closeOverlay')
    },

    sortFonts(type) {
      this.isSorting = true
      this.sortBy = type

      Ajax.request({
        url: this.url,

        onSuccess: (xhr) => {
          this.fonts = JSON.parse(xhr.responseText).items
        },

        onError: (xhr) => {
          alert(xhr)
        }
      })
    },

    addStyles() {
      let fontName
      let fontCss
      let style
      let styleText

      this.fontIndex = 1

      this.fonts.forEach((font, index) => {
        window.setTimeout(() => {
          fontName = font.family.replace(/\s+/g, '+')
          fontCss = `@import url('https://fonts.googleapis.com/css2?family=${fontName}');`
          style = document.createElement('style', fontCss)
          styleText = document.createTextNode(fontCss)
          style.type = 'text/css'
          style.appendChild(styleText)

          document.head.appendChild(style)

          // Show the fonts when the first 500 are loaded
          if (index > 500) {
            this.initialFontsLoaded = true
          }

          this.fontIndex = index
        }, 5 * index)
      })
    },

    setFont(font) {
      this.$emit('setFont', this.type, font)
    }
  },

  mounted() {
    this.getFonts()
  }
}
</script>

<style lang="sass" scoped>
  @import '../stylesheets/base/variables'
  @import '../stylesheets/helpers/mixins'

  .loading-fonts
    text-align: center

  .font-list
    display: flex
    flex-wrap: wrap
    align-items: center
    padding: 0
    list-style: none

    li
      flex-basis: 100%
      padding: 10px 0
      font-size: 28px
      cursor: pointer
      transition: all .2s ease-in-out
      text-align: center
      border-radius: 3px
      color: rgba(0, 0, 0, 0.8)
      border: 1px solid transparent
      border-radius: 3px

      @include media($media-gt-small)
        flex-basis: percentage(1/2)
        font-size: 18px

      @include media($media-gt-medium)
        flex-basis: percentage(1/3)
        font-size: 24px

      @include media($media-gt-large)
        flex-basis: percentage(1/5)
        font-size: 26px

      &:hover
        color: rgba(0, 0, 0, 1)
        border-color: rgba(0, 0, 0, 0.1)

  .font-controls
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center

    @include media($media-gt-medium)
      flex-direction: row
      margin-bottom: 50px

    .btn-wrapper
      margin: 5px
      opacity: 0.5

      &.is-active
        opacity: 1

    span
      font-size: 14px
      font-weight: bold
</style>
